import React, { useState, useEffect, useCallback } from 'react';
import { Grid, Typography, Box, Card, CardContent, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import FilterBar, { EMPTY_FILTERS } from '../components/FilterBar';
import StatCard from '../components/StatCard';
import { getMonthlyMessages } from '../utils/api';
import { MessageOutlined, SchoolOutlined, NotificationsOutlined, RepeatOutlined } from '@mui/icons-material';

const TT = { backgroundColor:'#111827', border:'1px solid rgba(0,191,165,0.2)', borderRadius:8 };

export default function MessagesPage() {
  const [data,    setData]    = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [view,    setView]    = useState('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getMonthlyMessages(filters);
      setData(r.data);
    } catch(e) { console.error(e); }
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const totals = data.reduce((acc, m) => ({
    sent:        acc.sent        + (m.total_sent        || 0),
    received:    acc.received    + (m.total_received    || 0),
    educational: acc.educational + (m.total_educational || 0),
    reminders:   acc.reminders   + (m.total_reminders   || 0),
    followup:    acc.followup    + (m.total_followup    || 0),
  }), { sent:0, received:0, educational:0, reminders:0, followup:0 });

  const chartData = data.map(m => ({
    month:       m.month,
    sent:        view==='mtn' ? m.mtn_sent       : view==='em' ? m.em_sent       : m.total_sent,
    received:    view==='mtn' ? m.mtn_received   : view==='em' ? m.em_received   : m.total_received,
    educational: view==='mtn' ? m.mtn_educational: view==='em' ? m.em_educational: m.total_educational,
    reminders:   view==='mtn' ? m.mtn_reminders  : view==='em' ? m.em_reminders  : m.total_reminders,
    followup:    view==='mtn' ? m.mtn_followup   : view==='em' ? m.em_followup   : m.total_followup,
  }));

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="primary.main">Message Analytics</Typography>
          <Typography variant="body2" color="text.secondary">Outbound / inbound volumes · Educational · Reminders · Follow-ups</Typography>
        </Box>
        <ToggleButtonGroup value={view} exclusive onChange={(_,v) => v && setView(v)} size="small"
          sx={{ '& .MuiToggleButton-root':{ fontFamily:'"IBM Plex Mono",monospace', fontSize:'0.72rem', px:2 } }}>
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="mtn">MTN</ToggleButton>
          <ToggleButton value="em">Eswatini Mobile</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <FilterBar filters={filters} onChange={setFilters} showGroup={false} />

      <Grid container spacing={2} mb={2}>
        <Grid item xs={6} sm={4} md={2.4}><StatCard title="Total Sent"     value={totals.sent.toLocaleString()}        icon={<MessageOutlined/>}      color="#00BFA5" loading={loading}/></Grid>
        <Grid item xs={6} sm={4} md={2.4}><StatCard title="Total Received" value={totals.received.toLocaleString()}    icon={<MessageOutlined/>}      color="#40C4FF" loading={loading}/></Grid>
        <Grid item xs={6} sm={4} md={2.4}><StatCard title="Educational"    value={totals.educational.toLocaleString()} icon={<SchoolOutlined/>}       color="#CE93D8" loading={loading}/></Grid>
        <Grid item xs={6} sm={4} md={2.4}><StatCard title="Reminders"      value={totals.reminders.toLocaleString()}   icon={<NotificationsOutlined/>} color="#FFB300" loading={loading}/></Grid>
        <Grid item xs={6} sm={4} md={2.4}><StatCard title="Follow-ups"     value={totals.followup.toLocaleString()}    icon={<RepeatOutlined/>}       color="#69F0AE" loading={loading}/></Grid>
      </Grid>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={7}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>Monthly Volume — Sent vs Received</Typography>
              <ResponsiveContainer width="100%" height={260}>
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="sG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#00BFA5" stopOpacity={0.3}/><stop offset="95%" stopColor="#00BFA5" stopOpacity={0}/></linearGradient>
                    <linearGradient id="rG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#40C4FF" stopOpacity={0.25}/><stop offset="95%" stopColor="#40C4FF" stopOpacity={0}/></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)"/>
                  <XAxis dataKey="month" tick={{ fontSize:11, fill:'#90A4AE', fontFamily:'IBM Plex Mono' }}/>
                  <YAxis tick={{ fontSize:11, fill:'#90A4AE' }}/>
                  <Tooltip contentStyle={TT}/><Legend/>
                  <Area type="monotone" dataKey="sent"     name="Sent"     stroke="#00BFA5" strokeWidth={2} fill="url(#sG)"/>
                  <Area type="monotone" dataKey="received" name="Received" stroke="#40C4FF" strokeWidth={2} fill="url(#rG)"/>
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" mb={2}>Outbound by Type</Typography>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)"/>
                  <XAxis dataKey="month" tick={{ fontSize:10, fill:'#90A4AE', fontFamily:'IBM Plex Mono' }}/>
                  <YAxis tick={{ fontSize:10, fill:'#90A4AE' }}/>
                  <Tooltip contentStyle={TT}/><Legend iconSize={10} wrapperStyle={{ fontSize:11 }}/>
                  <Bar dataKey="educational" name="Educational" stackId="a" fill="#CE93D8"/>
                  <Bar dataKey="reminders"   name="Reminders"   stackId="a" fill="#FFB300"/>
                  <Bar dataKey="followup"    name="Follow-up"   stackId="a" fill="#69F0AE" radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <CardContent>
          <Typography variant="h6" mb={2}>Message Type Trends</Typography>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)"/>
              <XAxis dataKey="month" tick={{ fontSize:11, fill:'#90A4AE', fontFamily:'IBM Plex Mono' }}/>
              <YAxis tick={{ fontSize:11, fill:'#90A4AE' }}/>
              <Tooltip contentStyle={TT}/><Legend/>
              <Line type="monotone" dataKey="educational" name="Educational" stroke="#CE93D8" strokeWidth={2} dot={false}/>
              <Line type="monotone" dataKey="reminders"   name="Reminders"   stroke="#FFB300" strokeWidth={2} dot={false}/>
              <Line type="monotone" dataKey="followup"    name="Follow-up"   stroke="#69F0AE" strokeWidth={2} dot={false}/>
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
